/*
 * Dehde — header behaviour (header proposal, Lotto B). Loaded (defer) by sections/header.liquid,
 * after the theme's global.js. No theme file is patched: this only drives the public methods of
 * the Urban custom elements.
 *
 * 1. Services strip (announcement bar, §4b)
 *    The theme's <marquee-component> starts itself 2s after construction (this.onLoad timer).
 *    From 992px, and at any width with prefers-reduced-motion, we cancel that timer so the strip
 *    stays a static row (layout in announcement-bar.css). If the viewport/preference changes later
 *    the marquee is started, paused or resumed accordingly. Cloned columns are hidden from
 *    assistive tech so the four messages are read once.
 *
 * 2. Hover-open dropdowns (§10) — release-safe
 *    Only when (min-width: 992px) and (hover: hover) and (pointer: fine), only on the level-1
 *    dropdowns of the main navigation. Open after 150ms, close 250ms after the pointer leaves.
 *    Click, keyboard and touch behaviour are the theme's, unchanged:
 *    - a panel opened by click is never closed by mouseleave;
 *    - clicking the label of a hover-opened panel keeps it open (pins it) instead of toggling it shut;
 *    - Escape closes a hover-opened panel even when focus is outside it.
 *    Menu 10x: Escape closes any open level-1 panel (hover- or click-opened); when focus was inside the
 *    panel it goes back to the panel's button.
 *
 * 3. Sticky header from the first scroll (menu 10x, "L'header resta fermo per i primi 2 secondi")
 *    The theme's <sticky-block> only calls initialize() from a 2s setTimeout (global.js, this.onLoad).
 *    We cancel that timer and initialize at once. global.js is not patched. Header behaviour = 'stacked'
 *    (sticky_header "always"): it measures the header's offset and height at init; endBounds
 *    (body height) is only read with data-hide-on-end, which the header does not set.
 *
 * 4. Measurement (menu 10x, "Come si misura")
 *    Click on any link inside a desktop panel or the mobile drawer pushes
 *    { event: 'menu_click', menu_group: <top item title>, menu_item: <link text> } to window.dataLayer,
 *    only if dataLayer already exists. No script or tag is added.
 */
(() => {
  /* ---------------------------------------------------------------- 1. strip */
  const wideMQ = window.matchMedia('(min-width: 992px)');
  const reduceMQ = window.matchMedia('(prefers-reduced-motion: reduce)');
  const motionAllowed = () => !wideMQ.matches && !reduceMQ.matches;

  const hideClones = (marquee) => {
    const track = marquee.querySelector('.marquee-component');
    if (!track) return;
    const mark = () => track.querySelectorAll('[class*="cloned"]:not([aria-hidden])').forEach((col) => col.setAttribute('aria-hidden', 'true'));
    mark();
    new MutationObserver(mark).observe(track, { childList: true });
  };

  customElements.whenDefined('marquee-component').then(() => {
    document.querySelectorAll('.announcement-bar marquee-component').forEach((marquee) => {
      hideClones(marquee);
      let state = 'theme'; // 'theme' = theme timer pending/running, 'deferred' = never started, 'paused'

      if (!motionAllowed()) {
        clearTimeout(marquee.onLoad);
        state = 'deferred';
      }

      // The theme resumes the marquee on mouseleave; keep it still where motion is not allowed.
      marquee.addEventListener('mouseleave', (event) => {
        if (!motionAllowed()) event.stopImmediatePropagation();
      }, true);

      const sync = () => {
        if (motionAllowed()) {
          if (state === 'deferred') marquee.initialize();
          else if (state === 'paused') marquee.setOnResume();
          state = 'theme';
          return;
        }
        if (state !== 'theme') return;
        if (marquee.initBounds === undefined) {
          clearTimeout(marquee.onLoad);
          state = 'deferred';
        } else {
          marquee.setOnPause();
          state = 'paused';
        }
      };
      wideMQ.addEventListener('change', sync);
      reduceMQ.addEventListener('change', sync);
    });
  });

  /* ------------------------------------------------------ 2. hover dropdowns */
  const hoverMQ = window.matchMedia('(min-width: 992px) and (hover: hover) and (pointer: fine)');
  const OPEN_DELAY = 150;
  const CLOSE_DELAY = 250;

  customElements.whenDefined('dropdown-component').then(() => {
    const dropdowns = Array.from(document.querySelectorAll(
      '.page-header .main-navigation > .main-navigation_item > dropdown-component[data-dropdown-level="1"]'
    )).filter((dd) => dd.button && dd.content);
    if (!dropdowns.length) return;

    const isOpen = (dd) => dd.button.getAttribute('aria-expanded') === 'true';
    const timers = new Map();
    const clearTimers = (dd) => {
      const t = timers.get(dd);
      if (!t) return;
      clearTimeout(t.open);
      clearTimeout(t.close);
    };

    dropdowns.forEach((dd) => {
      const item = dd.closest('.main-navigation_item') || dd;
      timers.set(dd, { open: null, close: null });

      item.addEventListener('mouseenter', () => {
        if (!hoverMQ.matches) return;
        const t = timers.get(dd);
        clearTimeout(t.close);
        if (isOpen(dd)) return;
        t.open = setTimeout(() => {
          if (isOpen(dd)) return;
          dropdowns.forEach((other) => {
            if (other === dd || !isOpen(other)) return;
            clearTimers(other);
            delete other.dataset.dehdeHover;
            other.close();
          });
          dd.open();
          dd.dataset.dehdeHover = 'true';
        }, OPEN_DELAY);
      });

      item.addEventListener('mouseleave', () => {
        const t = timers.get(dd);
        clearTimeout(t.open);
        if (dd.dataset.dehdeHover !== 'true') return; // opened by click/keyboard: theme rules
        t.close = setTimeout(() => {
          if (dd.dataset.dehdeHover === 'true' && isOpen(dd)) dd.close();
          delete dd.dataset.dehdeHover;
        }, CLOSE_DELAY);
      });

      // Click on the label of a hover-opened panel: pin it open instead of letting the theme toggle it shut.
      dd.addEventListener('click', (event) => {
        if (dd.dataset.dehdeHover !== 'true' || !dd.button.contains(event.target)) return;
        event.stopPropagation();
        clearTimers(dd);
        delete dd.dataset.dehdeHover;
      }, true);
    });

    document.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape') return;
      dropdowns.forEach((dd) => {
        clearTimers(dd);
        delete dd.dataset.dehdeHover;
        if (!isOpen(dd)) return;
        // The theme closes click-opened panels on keyup, and only while focus is inside; close all here.
        const focusInside = dd.contains(document.activeElement);
        dd.close();
        if (focusInside) dd.button.focus();
      });
    });
  });

  /* ------------------------------------------------------ 3. sticky at once */
  customElements.whenDefined('sticky-block').then(() => {
    document.querySelectorAll('sticky-block.sticky-header-wrapper').forEach((el) => {
      // onLoad undefined = the theme skipped init; behavior defined = the 2s timer already ran.
      if (el.onLoad === undefined || el.behavior !== undefined) return;
      clearTimeout(el.onLoad);
      el.initialize();
    });
  });

  /* ---------------------------------------------------- 4. menu_click event */
  const linkText = (a) => {
    let text = '';
    const walk = (node) => {
      node.childNodes.forEach((child) => {
        if (child.nodeType === Node.TEXT_NODE) text += child.textContent;
        else if (child.nodeType === Node.ELEMENT_NODE && !child.classList.contains('visually-hidden')) walk(child);
      });
    };
    walk(a);
    return text.replace(/\s+/g, ' ').trim();
  };

  document.addEventListener('click', (event) => {
    if (!window.dataLayer || typeof window.dataLayer.push !== 'function') return;
    const a = event.target.closest && event.target.closest('a[href]');
    if (!a) return;
    const inPanel = a.closest('.page-header .main-navigation_dropdown-wrapper--level1');
    const inDrawer = a.closest('.modal-component--menu-drawer');
    if (!inPanel && !inDrawer) return;
    const item = a.closest('[data-dehde-group]');
    let group = item ? item.dataset.dehdeGroup : '';
    if (!group) {
      const label = a.closest('.dehde-drawer_section')?.querySelector('.dehde-drawer_label');
      const heading = document.getElementById('modal-menu-drawer-heading');
      group = (label || heading)?.textContent.trim() || '';
    }
    window.dataLayer.push({ event: 'menu_click', menu_group: group, menu_item: linkText(a) });
  }, true);
})();
